import type { TeacherClass, SchoolYearOption } from 'src/class/class-provider';

import { parseMinutes } from '@classroom-ai/pacing';

// ----------------------------------------------------------------------
// Pure helpers for the teacher header (epic #617): the class title, the class menu grouped by
// course, and the rule for when the school / year needs a chip at all.

/** Same rule as `defaultSchedulePeriod` in class-provider (kept local so this file stays pure). */
function defaultSchedulePeriod(c: TeacherClass) {
  return c.schedulePeriods?.find((sp) => sp.dailySchedule.isDefault)?.schedulePeriod ?? null;
}

/** "Algebra 1 · Q2 · Period 3" — the full class identity (banners, aria labels, other pages). */
export function classLabel(c: TeacherClass): string {
  const period = defaultSchedulePeriod(c);
  return [c.course.name, c.academicPeriod?.name, period?.name].filter(Boolean).join(' · ');
}

/** "Math 7 · Period 3" — the header title (the term is in the menu footer's year). */
export function classTitle(c: TeacherClass): string {
  const period = defaultSchedulePeriod(c);
  return [c.course.name, period?.name ?? c.academicPeriod?.name].filter(Boolean).join(' · ');
}

/** "P3" from "Period 3"; "HR" from "Homeroom"; the course's initials when no period is set. */
export function periodBadge(c: TeacherClass): string {
  const name = defaultSchedulePeriod(c)?.name ?? '';
  const digits = /(\d+)/.exec(name);
  if (digits) return `P${digits[1]}`;
  const words = (name || c.course.name).split(/\s+/).filter(Boolean);
  const initials = words.length > 1 ? words.slice(0, 2).map((w) => w[0]) : [...(words[0] ?? '?').slice(0, 2)];
  return initials.join('').toUpperCase();
}

/** "08:30" → "8:30". Returns the input unchanged if it isn't HH:mm. */
export function clock(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]) % 12 || 12;
  return `${h}:${m[2]}`;
}

/** "9:40–10:25" under the default daily schedule, or null when the class has no period. */
export function periodTime(c: TeacherClass): string | null {
  const p = defaultSchedulePeriod(c);
  if (!p?.startTime) return null;
  return p.endTime ? `${clock(p.startTime)}–${clock(p.endTime)}` : clock(p.startTime);
}

function startMinutes(c: TeacherClass): number {
  const p = defaultSchedulePeriod(c);
  return (p?.startTime ? parseMinutes(p.startTime) : null) ?? Number.MAX_SAFE_INTEGER;
}

export type CourseGroup = { courseId: string; courseName: string; classes: TeacherClass[] };

/**
 * Classes grouped by course for the class menu. Within a course, sections run in bell order;
 * courses run in the order of their first section of the day (ties by name).
 */
export function groupByCourse(classes: TeacherClass[]): CourseGroup[] {
  const m = new Map<string, CourseGroup>();
  for (const c of classes) {
    const g = m.get(c.course.id) ?? { courseId: c.course.id, courseName: c.course.name, classes: [] };
    g.classes.push(c);
    m.set(c.course.id, g);
  }
  const byBell = (a: TeacherClass, b: TeacherClass) =>
    startMinutes(a) - startMinutes(b) || classTitle(a).localeCompare(classTitle(b));
  const groups = [...m.values()];
  groups.forEach((g) => g.classes.sort(byBell));
  return groups.sort(
    (a, b) => startMinutes(a.classes[0]) - startMinutes(b.classes[0]) || a.courseName.localeCompare(b.courseName)
  );
}

export type ContextChip = { label: string; tone: 'warning' | 'default'; tooltip: string };

/**
 * Whether the header shows a school / year chip. A teacher with one school in the ACTIVE year
 * sees nothing; the school and year live in the class menu footer and the account drawer.
 * - More than one school → the school's name (neutral), so you always know which one.
 * - A year that is known not to be the current one → amber, with the year.
 *   A year with no status (reached only through a class) is "not current" only when the same
 *   school has an ACTIVE year to compare it with.
 */
export function contextChip(active: SchoolYearOption | null, options: SchoolYearOption[]): ContextChip | null {
  if (!active) return null;
  const multiSchool = new Set(options.map((o) => o.schoolId)).size > 1;
  const schoolHasActive = options.some((o) => o.schoolId === active.schoolId && o.status === 'ACTIVE');
  const notCurrent = active.status ? active.status !== 'ACTIVE' : schoolHasActive;
  if (!multiSchool && !notCurrent) return null;

  if (!notCurrent) {
    return { label: active.schoolName, tone: 'default', tooltip: `${active.schoolName} · ${active.yearName}` };
  }
  const kind = active.status === 'DRAFT' ? 'draft year' : active.status === 'ARCHIVED' ? 'past year' : 'not the current year';
  const label = [multiSchool ? active.schoolName : null, `${active.yearName} (${kind})`].filter(Boolean).join(' · ');
  return {
    label,
    tone: 'warning',
    tooltip: `You're looking at ${active.schoolName} · ${active.yearName}, not the current school year.`,
  };
}

/** "ed360 Middle School · 2026–2027" — the class menu footer. */
export function schoolYearLabel(o: SchoolYearOption | null): string {
  return o ? `${o.schoolName} · ${o.yearName}` : 'No school year';
}

// ----------------------------------------------------------------------
// Now / next (epic #617 P2) — from today's meetings as `useTeacherDays` resolves them, so an
// alternate bell schedule (Early Release) moves the times, and a holiday or weekend has none.

export type TodayMeeting = { classId: string; startTime: string; endTime: string };

export type PeriodState = {
  state: 'now' | 'next' | 'done' | 'later';
  /** "9:40–10:25" — today's actual times. */
  time: string;
  startTime: string;
};

/**
 * Each meeting class's state at `nowMinutes` (minutes since midnight): `now` while in session,
 * `next` for the first one still to start (only one class is next), `done` once ended, else
 * `later`. Classes that don't meet today are absent from the map.
 */
export function periodStates(meetings: TodayMeeting[], nowMinutes: number): Map<string, PeriodState> {
  const out = new Map<string, PeriodState>();
  const timed = meetings
    .map((m) => ({ ...m, start: parseMinutes(m.startTime), end: parseMinutes(m.endTime) }))
    .filter((m): m is TodayMeeting & { start: number; end: number } => m.start != null && m.end != null)
    .sort((a, b) => a.start - b.start);
  const next = timed.find((m) => m.start > nowMinutes) ?? null;
  for (const m of timed) {
    const state =
      m.start <= nowMinutes && nowMinutes < m.end ? 'now' : m.end <= nowMinutes ? 'done' : m === next ? 'next' : 'later';
    out.set(m.classId, { state, time: `${clock(m.startTime)}–${clock(m.endTime)}`, startTime: m.startTime });
  }
  return out;
}

/**
 * The class title's second line: "In session · 9:40–10:25 · 6 practicing", "Next · 12:15",
 * "Done today · 8:00–8:45", or the default time + grade when the class doesn't meet today.
 */
export function titleSubtitle(c: TeacherClass, state: PeriodState | undefined, practicing: number): string {
  const live = practicing > 0 ? `${practicing} practicing` : null;
  let head: string[];
  if (state?.state === 'now') head = ['In session', state.time];
  else if (state?.state === 'next') head = [`Next · ${clock(state.startTime)}`];
  else if (state?.state === 'done') head = ['Done today', state.time];
  else if (state) head = [`Today ${state.time}`];
  else head = [periodTime(c), c.course.gradeLevel ? `Grade ${c.course.gradeLevel}` : null].filter((x): x is string => !!x);
  return [...head, live].filter(Boolean).join(' · ');
}
